import {RoutePath} from '@/components/layout/navigation';
import {Plural, Trans} from '@lingui/macro';
import {MerchantsInPeriodicReviewAccountListOutput} from '@zentact/api/src/trpc/routers/merchantAccountRouter';
import {MerchantAccountPublicStatus} from '@zentact/common';
import {AlertBanner} from '@zentact/ui-tailwind/elements/AlertBanner';
import {DateTime} from 'luxon';
import {useNavigate} from 'react-router-dom';

const getEarliestExpirationDate = (accounts: MerchantsInPeriodicReviewAccountListOutput) => {
  const sortedDates = accounts
    .map(account => DateTime.fromISO(account.inPeriodicReviewExpirationDate))
    .sort((a, b) => a.toMillis() - b.toMillis());

  if (!sortedDates[0]) {
    throw new Error('No expiration date found');
  }

  return sortedDates[0];
};

const calculateDaysToRespond = (expirationDate: DateTime) =>
  Math.floor(Math.abs(DateTime.now().diff(expirationDate, 'days').days));

interface MerchantReviewAllBannerProps {
  merchantAccountsInPeriodicReview?: MerchantsInPeriodicReviewAccountListOutput;
}
export const MerchantReviewAllBanner = ({
  merchantAccountsInPeriodicReview,
}: MerchantReviewAllBannerProps) => {
  if (!merchantAccountsInPeriodicReview?.length) return null;
  const navigate = useNavigate();

  const earliestExpirationDate = getEarliestExpirationDate(merchantAccountsInPeriodicReview);
  const daysToRespond = calculateDaysToRespond(earliestExpirationDate);
  const merchantsCount = merchantAccountsInPeriodicReview.length;

  return (
    <AlertBanner
      alertTitle={
        <Trans>
          {merchantsCount} <Plural value={merchantsCount} one="merchant" other="merchants" /> review
          required,{' '}
          {daysToRespond === 0 ? (
            'less than a day'
          ) : (
            <>
              {daysToRespond} <Plural value={daysToRespond} one="day" other="days" />
            </>
          )}{' '}
          to respond
        </Trans>
      }
      alertMessage={
        <Trans>
          You have{' '}
          <Plural
            value={merchantsCount}
            one="a merchant at risk because their application needs review by Adyen."
            other="# merchants at risk because their applications need review by Adyen."
          />
        </Trans>
      }
      alertType="warning"
      rightButtonLabel={
        <Trans>
          View <Plural value={merchantsCount} one="Merchant" other="# Merchants" />
        </Trans>
      }
      rightButtonAction={() =>
        navigate(
          `${RoutePath.MERCHANT_ACCOUNTS}?status=${MerchantAccountPublicStatus.ACTIVE_IN_PERIODIC_REVIEW}`
        )
      }
    />
  );
};
